import { z } from 'zod';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];

const isValidFileType = (file: File) =>
  ACCEPTED_IMAGE_TYPES.includes(file.type);

const profilePictureFileSchema = z
  .custom<File>((value) => value instanceof File)
  .refine(
    (file) => file.size <= MAX_FILE_SIZE,
    `File size should be less than 5MB.`
  )
  .refine(
    (file) => isValidFileType(file),
    'Only .jpg, .jpeg, .png and .webp formats are supported.'
  );

const profilePictureUrlSchema = z
  .string()
  .url()
  .refine(async (url) => {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      const contentType = response.headers.get('content-type');
      return contentType ? ACCEPTED_IMAGE_TYPES.includes(contentType) : false;
    } catch {
      return false;
    }
  }, 'Invalid image URL or unsupported image type.');

export const loginSchema = z.object({
  email: z
    .string({
      required_error: 'Email is required',
    })
    .email({
      message: 'Please enter a valid email address',
    }),
  password: z.string().min(8, {
    message: 'Password must be at least 8 characters long',
  }),
});

export const usernameSchema = z
  .string({
    required_error: 'Username is required',
  })
  .trim()
  .min(4, {
    message: 'Username must be at least 4 characters long',
  })
  .max(15, {
    message: 'Username must not exceed 15 characters',
  })
  .regex(/^[a-zA-Z0-9_]+$/, {
    message: 'Username can only contain letters, numbers, and underscores',
  })
  .regex(/^[a-zA-Z]/, {
    message: 'Username must start with a letter',
  })
  .regex(/^(?!.*_{2,}).*$/, {
    message: 'Username cannot contain consecutive underscores',
  })
  .refine((val) => !/\s/.test(val), {
    message: 'Username cannot contain spaces or other whitespace characters',
  });

export const signupBasicDetailsWithEmailSchema = z
  .object({
    profilePicture: z
      .union([profilePictureFileSchema, profilePictureUrlSchema])
      .optional(),
    username: usernameSchema,
    firstname: z
      .string({
        required_error: 'Firstname is required',
      })
      .trim()
      .min(1, {
        message: 'Firstname is required',
      }),
    lastname: z
      .string({
        required_error: 'Lastname is required',
      })
      .trim()
      .min(1, {
        message: 'Lastname is required',
      }),
    email: z
      .string({
        required_error: 'Email is required',
      })
      .trim()
      .email({
        message: 'Please enter a valid email address',
      }),
    password: z
      .string({
        required_error: 'Password is required',
      })
      .trim()
      .min(8, {
        message: 'Password must be at least 8 characters long',
      }),
    confirmPassword: z
      .string({
        required_error: 'Confirm password is required',
      })
      .trim()
      .min(8, {
        message: 'Password must be at least 8 characters long',
      }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

export const signupBasicDetailsWithGoogleSchema = z.object({
  profilePicture: z
    .union([profilePictureFileSchema, profilePictureUrlSchema])
    .optional(),
  username: usernameSchema,
  firstname: z
    .string({
      required_error: 'Firstname is required',
    })
    .trim()
    .min(1, {
      message: 'Firstname is required',
    }),
  lastname: z
    .string({
      required_error: 'Lastname is required',
    })
    .trim()
    .min(1, {
      message: 'Lastname is required',
    }),
  email: z
    .string({
      required_error: 'Email is required',
    })
    .trim()
    .email({
      message: 'Please enter a valid email address',
    }),
});

export const signupOtherDetailsSchema = z.object({
  locatedAt: z.string().min(1, {
    message: 'Located at is required',
  }),
  cncBrand: z.array(z.string()).min(1, {
    message: 'At least one CNC Brand is required',
  }),
  cncModel: z.array(z.string()).min(1, {
    message: 'At least one CNC Model is required',
  }),
  cncVariant: z.array(z.string()).min(1, {
    message: 'At least one CNC Variant is required',
  }),
});

export const forgetPasswordSchema = z.object({
  email: z.string().email(),
});

export const resetPasswordSchema = z
  .object({
    password: z
      .string({
        required_error: 'Password is required',
      })
      .trim()
      .min(8, {
        message: 'Password must be at least 8 characters long',
      }),
    confirmPassword: z
      .string({
        required_error: 'Confirm password is required',
      })
      .trim()
      .min(8, {
        message: 'Password must be at least 8 characters long',
      }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

export const changePasswordSchema = z
  .object({
    currentPassword: z
      .string({
        required_error: 'Password is required',
      })
      .trim()
      .min(8, {
        message: 'Password must be at least 8 characters long',
      }),
    newPassword: z
      .string({
        required_error: 'Password is required',
      })
      .trim()
      .min(8, {
        message: 'Password must be at least 8 characters long',
      }),
    confirmPassword: z
      .string({
        required_error: 'Confirm password is required',
      })
      .trim()
      .min(8, {
        message: 'Password must be at least 8 characters long',
      }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords must match',
    path: ['confirmPassword'],
  });

export const editProfileFormSchema = z.object({
  firstname: z
    .string()
    .min(2, { message: 'First name must be at least 2 characters' })
    .max(50, { message: 'First name must not exceed 50 characters' }),
  lastname: z
    .string()
    .min(2, { message: 'Last name must be at least 2 characters' })
    .max(50, { message: 'Last name must not exceed 50 characters' }),
  profilePicture: z
    .union([profilePictureFileSchema, profilePictureUrlSchema])
    .optional(),
});

export const addMachineSchema = z.object({
  cncBrand: z.array(z.string()).min(1, {
    message: 'At least one CNC Brand is required',
  }),
  cncModel: z.array(z.string()).min(1, {
    message: 'At least one CNC Model is required',
  }),
  cncVariant: z.array(z.string()).min(1, {
    message: 'At least one CNC Variant is required',
  }),
});
